import React, { useEffect, useState } from "react";
import { trackNonInteractionEvent } from "@leafly-com/web-utils";
import { useSelector } from "react-redux";

import { getItemsForUser } from "api/requests/getItemsForUser";
import getMenuItemsByIds from "api/requests/getMenuItemsV2ByIds";
import { Action, Category } from "constants/events";
import { MenuItem } from "custom-types/MenuItem";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useIsJaneCheckoutEnabled } from "hooks/useIsJaneCheckoutEnabled";
import { getIsCartRecreational } from "redux/selectors/cart";
import { trackEvent } from "utils/trackEvent";

import Carousel from "components/botanic/Carousel";
import MenuItemCard from "components/botanic/MenuItemCard";
import { MenuItemCardActions } from "components/Shared/MenuItemCarousel/MenuItemCardActions";
import { MenuCardSkeleton } from "components/Shared/Skeletons";

const ReorderCarousel: React.FC<{
  dispensaryId?: number;
  itemIdsToExclude?: number[];
  isBagPage?: boolean;
  title?: string;
  onReservation?: () => void;
  className?: string;
}> = ({
  dispensaryId,
  itemIdsToExclude = [],
  isBagPage = false,
  title = "Items from recent orders",
  onReservation = () => {},
  className,
}) => {
  const [currentItems, setCurrentItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);

  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();
  const domainCountryCode = useDomainCountryCode();
  const isCartRec = useSelector(getIsCartRecreational);

  useEffect(() => {
    setLoading(true);

    getItemsForUser(domainCountryCode, dispensaryId).then(
      async ({ cartItems }) => {
        const previouslyOrderedItemIds = cartItems.map(
          ({ menu_item_id }) => menu_item_id
        );

        const menuItemIds = previouslyOrderedItemIds.filter(
          (id) => !itemIdsToExclude.includes(id)
        );

        if (menuItemIds.length) {
          const { data } = await getMenuItemsByIds(menuItemIds);

          // On bag page, show only items that match the cart type,
          // this will ensure the user is not prompted to start a new cart when adding items to cart from reorder carousel.
          if (isBagPage) {
            const filteredData = data.filter((item) =>
              item.variants.some((variant) => {
                return isCartRec ? !variant.medical : variant.medical;
              })
            );

            setCurrentItems(filteredData);
          } else {
            setCurrentItems(data);
          }

          trackNonInteractionEvent(
            Category.orderAgainCarousel,
            "Impression",
            "Menu Item"
          );
        } else {
          setCurrentItems([]);
        }
        setLoading(false);
      }
    );
  }, [dispensaryId, JSON.stringify(itemIdsToExclude)]);

  return !loading && currentItems.length === 0 ? (
    <></>
  ) : (
    <Carousel
      title={title}
      className={className}
      data-testid="reorder-carousel"
    >
      {loading
        ? new Array(4)
            .fill(" ")
            .map((_, ind) => (
              <MenuCardSkeleton
                key={ind}
                className="bg-white elevation-low rounded"
              />
            ))
        : currentItems.map((menuItem) => (
            <MenuItemCard
              key={menuItem.id}
              menuItem={menuItem}
              handleCardClick={() =>
                trackEvent(
                  Category.orderAgainCarousel,
                  Action.click,
                  "Menu Item"
                )
              }
              showFulfillment={true}
            >
              <MenuItemCardActions
                menuItem={menuItem}
                onClick={() =>
                  trackEvent(
                    Category.orderAgainCarousel,
                    Action.click,
                    "add to bag",
                    { janeCheckoutEnabled: isJaneCheckoutEnabled }
                  )
                }
                onReservation={() => onReservation()}
              />
            </MenuItemCard>
          ))}
    </Carousel>
  );
};

export default ReorderCarousel;
